import React, { useEffect, useState } from 'react';
import { Menu, X, Phone, UserCircle, ChevronDown } from 'lucide-react';
import { Button } from './ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null);
  // Handle scroll effect for shadow
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);
  const classButtons = [{
    name: 'In-Person Classes',
    subtitle: '(Live-classroom)',
    href: '/LiveClasses.html',
    color: 'bg-[#F5A623] hover:bg-[#E09612]'
  }, {
    name: 'Zoom Classes',
    subtitle: '(Live-virtual)',
    href: '/LiveClasses.html',
    color: 'bg-[#F5A623] hover:bg-[#E09612]'
  }, {
    name: 'On-Demand',
    subtitle: '(Self-Paced 24/7)',
    href: '/Products',
    color: 'bg-[#F5A623] hover:bg-[#E09612]'
  }];
  const navigationMenu = [{
    name: 'Home',
    href: '/',
    type: 'link'
  }, {
    name: 'Classes',
    type: 'dropdown',
    items: [{
      name: 'On-Demand CE - Self Paced 24/7',
      href: '/Products'
    }, {
      name: 'Zoom Classes - Live-virtual',
      href: '/LiveClasses.html'
    }, {
      name: 'In-Person Classes - Live-classroom',
      href: '/LiveClasses.html'
    }]
  }, {
    name: 'License Information',
    type: 'dropdown',
    items: [{
      name: 'License Information',
      href: '/License_Information'
    }, {
      name: 'How To Report Classes To ADRE',
      href: '/How_To_Add_CE_Classes'
    }]
  }, {
    name: 'Blog',
    href: '/Blog',
    type: 'link'
  }, {
    name: 'About',
    type: 'dropdown',
    items: [{
      name: 'About Desert Sage Seminars',
      href: '/About'
    }, {
      name: 'Desert Sage Seminars Instructors',
      href: '/Instructors'
    }]
  }, {
    name: 'Help Zone',
    type: 'dropdown',
    items: [{
      name: 'FAQs (Frequently Asked Questions)',
      href: '/16.html'
    }, {
      name: 'Lost Certificates',
      href: '/11.html'
    }, {
      name: 'Recover password',
      href: '/14.html'
    }, {
      name: 'Refund Policy',
      href: '/45.html'
    }, {
      name: 'Privacy Statement',
      href: '/15.html'
    }, {
      name: 'Contact Us',
      href: '/Contact'
    }]
  }, {
    name: 'Links & Resources',
    type: 'dropdown',
    items: [{
      name: 'ADRE & Useful Links',
      href: '/Links&Resources'
    }, {
      name: 'Arizona Local Associations',
      href: '/Arizona_Local_Associations'
    }]
  }];
  const toggleMobileDropdown = (name: string) => {
    setOpenMobileDropdown(openMobileDropdown === name ? null : name);
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-md' : ''}`} data-id="element-286">
      {/* Top Bar - SEO Text & Contact Info (Desktop Only) */}
      <div className="bg-brand-purple-900 text-white py-1.5 hidden lg:block" data-id="element-287">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm font-medium" data-id="element-288">
          <div className="text-white" data-id="element-289">
            Arizona's Trusted ADRE-Approved Real Estate Continuing Education
            Provider
          </div>
          <div className="flex items-center space-x-8" data-id="element-290">
            <div className="flex items-center space-x-2 text-[#F5A623]" data-id="element-291">
              <Phone className="w-4 h-4" data-id="element-292" />
              <span data-id="element-293">Customer Service:</span>
              <span className="font-bold text-white" data-id="element-294">602-377-6207</span>
            </div>
            <a href="/Contact" className="text-white hover:text-[#F5A623] transition-colors" data-id="element-295">
              Contact Us
            </a>
          </div>
        </div>
      </div>

      {/* Main Header Bar - Logo + Class Buttons + Login */}
      <div className="bg-white border-b border-gray-200" data-id="element-296">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-297">
          <div className="flex justify-between items-center h-16 lg:h-18" data-id="element-298">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center" data-id="element-299">
              <a href="/" className="flex items-center group" data-id="element-300">
                <img src="https://cdn.magicpatterns.com/uploads/6EDXC2FVxZjvEzYwwvW1XK/image.png" alt="Desert Sage Seminars" className="h-12 lg:h-14 w-auto" data-id="element-301" />
              </a>
            </div>

            {/* Desktop - Class Buttons + Login */}
            <div className="hidden lg:flex items-center space-x-6" data-id="element-302">
              {/* Class Buttons */}
              <div className="flex items-center space-x-3" data-id="element-303">
                {classButtons.map(button => <a key={button.name} href={button.href} className={`px-5 py-2 flex flex-col items-center justify-center text-center rounded-md shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 border border-[#E09612]/20 ${button.color}`} data-id="element-304">
                    <span className="text-sm font-bold text-brand-purple-900" data-id="element-305">
                      {button.name}
                    </span>
                    <span className="text-xs font-medium text-brand-purple-900/80" data-id="element-306">
                      {button.subtitle}
                    </span>
                  </a>)}
              </div>

              <div className="h-10 w-px bg-gray-200 mx-2" data-id="element-307"></div>

              {/* Login Button */}
              <Button className="bg-brand-purple-600 hover:bg-brand-purple-700 text-white font-bold px-6 h-[52px] shadow-md hover:shadow-lg flex items-center gap-2 border border-brand-purple-700" data-id="element-308">
                <UserCircle className="w-5 h-5" data-id="element-309" />
                <a href="/login-register" data-id="element-310">Log In / Register</a>
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center" data-id="element-311">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-brand-purple-900 hover:bg-brand-purple-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-purple-500" data-id="element-312">
                <span className="font-bold mr-2" data-id="element-313">Menu</span>
                {isMenuOpen ? <X className="block h-6 w-6" aria-hidden="true" data-id="element-314" /> : <Menu className="block h-6 w-6" aria-hidden="true" data-id="element-315" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Menu Bar - Desktop Only */}
      <div className="hidden lg:block bg-white border-b border-gray-100 shadow-sm" data-id="element-316">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-317">
          <nav className="flex items-center justify-center space-x-1 py-2" data-id="element-318">
            {navigationMenu.map(item => <div key={item.name} className="relative" data-id="element-319">
                {item.type === 'link' ? <a href={item.href} className="px-4 py-2 text-base font-bold text-black hover:text-brand-purple-600 hover:bg-gray-50 rounded-md transition-colors" data-id="element-320">
                    {item.name}
                  </a> : <div className="relative" onMouseEnter={() => setOpenDropdown(item.name)} onMouseLeave={() => setOpenDropdown(null)} data-id="element-321">
                    <button className="flex items-center px-4 py-2 text-base font-bold text-black hover:text-brand-purple-600 hover:bg-gray-50 rounded-md transition-colors" onClick={e => {
                e.stopPropagation();
                setOpenDropdown(openDropdown === item.name ? null : item.name);
              }} data-id="element-322">
                      {item.name}
                      <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${openDropdown === item.name ? 'rotate-180' : ''}`} data-id="element-323" />
                    </button>

                    <AnimatePresence data-id="element-324">
                      {openDropdown === item.name && <motion.div initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.2
                }} className="absolute left-0 mt-1 w-72 bg-white rounded-md shadow-lg border border-gray-100 py-2 z-50" data-id="element-325">
                          {item.items?.map(subItem => <a key={subItem.name} href={subItem.href} className="block px-4 py-2.5 text-base text-gray-700 hover:bg-brand-purple-50 hover:text-brand-purple-700 transition-colors" data-id="element-326">
                              {subItem.name}
                            </a>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div>}
              </div>)}
          </nav>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence data-id="element-327">
        {isMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="lg:hidden bg-white border-t border-gray-100 overflow-hidden shadow-lg max-h-[calc(100vh-5rem)] overflow-y-auto" data-id="element-328">
            <div className="px-4 pt-4 pb-6 space-y-2" data-id="element-329">
              {/* SEO Text - Mobile */}
              <div className="text-center text-sm font-medium text-brand-purple-900 pb-3 mb-3 border-b border-gray-100" data-id="element-330">
                Arizona's Trusted ADRE-Approved Real Estate Continuing Education
                Provider
              </div>

              {/* Class Buttons - Mobile */}
              <div className="space-y-2 pb-3 mb-3 border-b border-gray-100" data-id="element-331">
                {classButtons.map(button => <a key={button.name} href={button.href} className={`block px-4 py-2 rounded-md text-center shadow-md border border-[#E09612]/20 ${button.color}`} onClick={() => setIsMenuOpen(false)} data-id="element-332">
                    <div className="text-base font-bold text-brand-purple-900" data-id="element-333">
                      {button.name}
                    </div>
                    <div className="text-sm font-medium text-brand-purple-900/80" data-id="element-334">
                      {button.subtitle}
                    </div>
                  </a>)}
              </div>

              {/* Navigation Menu - Mobile */}
              {navigationMenu.map(item => <div key={item.name} data-id="element-335">
                  {item.type === 'link' ? <a href={item.href} className="block px-4 py-3 text-base font-bold text-black hover:bg-brand-purple-50 hover:text-brand-purple-700 rounded-md transition-colors" onClick={() => setIsMenuOpen(false)} data-id="element-336">
                      {item.name}
                    </a> : <div data-id="element-337">
                      <button onClick={() => toggleMobileDropdown(item.name)} className="flex items-center justify-between w-full px-4 py-3 text-base font-bold text-black hover:bg-brand-purple-50 hover:text-brand-purple-700 rounded-md transition-colors" data-id="element-338">
                        {item.name}
                        <ChevronDown className={`h-4 w-4 transition-transform ${openMobileDropdown === item.name ? 'rotate-180' : ''}`} data-id="element-339" />
                      </button>

                      <AnimatePresence data-id="element-340">
                        {openMobileDropdown === item.name && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="ml-4 mt-1 space-y-1 overflow-hidden" data-id="element-341">
                            {item.items?.map(subItem => <a key={subItem.name} href={subItem.href} className="block px-4 py-2 text-sm text-gray-600 hover:bg-brand-purple-50 hover:text-brand-purple-700 rounded-md transition-colors" onClick={() => setIsMenuOpen(false)} data-id="element-342">
                                {subItem.name}
                              </a>)}
                          </motion.div>}
                      </AnimatePresence>
                    </div>}
                </div>)}

              {/* Contact & Login - Mobile */}
              <div className="pt-4 mt-4 border-t border-gray-100 space-y-3" data-id="element-343">
                <div className="flex items-center justify-center text-brand-purple-900 bg-gray-50 py-3 rounded-lg" data-id="element-344">
                  <Phone className="w-5 h-5 mr-2 text-[#F5A623]" data-id="element-345" />
                  <span className="font-medium mr-2 text-[#F5A623]" data-id="element-346">
                    Customer Service:
                  </span>
                  <span className="font-bold" data-id="element-347">602-377-6207</span>
                </div>

                <Button className="w-full bg-brand-purple-600 hover:bg-brand-purple-700 text-white font-bold py-3 flex justify-center items-center gap-2" onClick={() => setIsMenuOpen(false)} data-id="element-348">
                  <UserCircle className="w-5 h-5" data-id="element-349" />
                  <a href="/login-register" data-id="element-350">Log In / Register</a>
                </Button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}